import { createBrowserHistory } from 'history';
import React, { lazy, useEffect } from 'react';
import { Route, Switch, BrowserRouter, matchPath, useLocation } from 'react-router-dom';
import { GuardProvider } from 'react-router-guards';
import {
	GuardToRoute,
	Next,
	GuardFunctionRouteProps,
	GuardFunction,
} from 'react-router-guards/dist/types';

import { setCurrentRouteName } from 'modules/core/monitoring';

const NotFoundPage = lazy(() => import('./not-found.page'));
const PaymentPage = lazy(
	() => import('modules/payment-request/payment-request.page'),
);
const SubscriptionPlanPage = lazy(
	() => import('modules/subscription-plan/subscription-plan.page'),
);
const SubscriptionPage = lazy(
	() => import('modules/subscription/subscription.page'),
);
const OpenBankingRedirectPage = lazy(
	() => import('modules/open-banking-redirect/open-banking-redirect.page'),
);
const KlarnaRedirectPage = lazy(
	() => import('modules/klarna-redirect/klarna-redirect.page'),
);
const AffirmRedirectPage = lazy(
	() => import('modules/affirm-redirect/affirm-redirect.page'),
);
const SunbitRedirectPage = lazy(
	() => import('modules/sunbit-redirect/sunbit-redirect.page'),
);
const PaymentLinkInactivePage = lazy(
	() => import('modules/payment-link-inactive/payment-link-inactive.page'),
);

// Route pattern table. Keep in sync with the <Route> list below — drives
// both rendering and New Relic route-name tagging, so sensitive IDs
// (paymentRequestId, subscriptionId, etc.) never land in observability data.
const ROUTE_PATTERNS = [
	'/paypoint/:payPointId',
	'/request/:paymentRequestId',
	'/subscription-plan/:subscriptionPlanId',
	'/subscription/:subscriptionId',
	'/open-banking/redirect',
	'/klarna/redirect',
	'/affirm/redirect',
	'/sunbit/redirect',
	'/payment-link-inactive',
	'/',
];

// Create and export browser history
export const history = createBrowserHistory();

const RouteTracker: React.FC = () => {
	const location = useLocation();

	useEffect(() => {
		const matched = ROUTE_PATTERNS.find((pattern) =>
			matchPath(location.pathname, { path: pattern, exact: true }),
		);
		setCurrentRouteName(matched ?? 'unknown');
	}, [location.pathname]);

	return null;
};

/** Router component containing all application routes */
const AppRouter: React.FC = () => {
	// TODO - Get auth from state
	const hasAuth = false;

	/** Route guard for routes requiring login */
	const requireLogin: GuardFunction = async (
		to: GuardToRoute,
		_: GuardFunctionRouteProps | null,
		next: Next,
	) => {
		// If route requires auth
		if (to.meta.auth) {
			hasAuth ? next() : next.redirect('/');
		} else {
			next();
		}
	};

	return (
		<BrowserRouter>
			<RouteTracker />
			<GuardProvider guards={[requireLogin]} error={NotFoundPage}>
				<Switch>
					<Route exact path="/" component={PaymentPage} />
					<Route
						exact
						path="/paypoint/:payPointId"
						component={PaymentPage}
					/>
					<Route
						exact
						path="/request/:paymentRequestId"
						component={PaymentPage}
					/>
					<Route
						exact
						path="/subscription-plan/:subscriptionPlanId"
						component={SubscriptionPlanPage}
					/>
					<Route
						exact
						path="/subscription/:subscriptionId"
						component={SubscriptionPage}
					/>
					<Route
						exact
						path="/open-banking/redirect"
						component={OpenBankingRedirectPage}
					/>
					<Route
						exact
						path="/klarna/redirect"
						component={KlarnaRedirectPage}
					/>
					<Route
						exact
						path="/affirm/redirect"
						component={AffirmRedirectPage}
					/>
					<Route
						exact
						path="/sunbit/redirect"
						component={SunbitRedirectPage}
					/>
					<Route
						exact
						path="/payment-link-inactive"
						component={PaymentLinkInactivePage}
					/>
					<Route component={NotFoundPage} />
				</Switch>
			</GuardProvider>
		</BrowserRouter>
	);
};

export default AppRouter;
